import { useCurrentStoreId } from '@hooks/useCurrentStore'
import { useScanId } from '@hooks/useScan'

import httpClient, { type RequestData, type UrlParam, type HttpClient } from './HttpClient'

import type { StoreContext } from '../provider/StoreContext'

/**
 * Context-aware HTTP client that automatically injects store and scan IDs into API requests.
 *
 * This client provides the same interface as HttpClient but automatically prefixes
 * all URLs with `/api/store/{storeId}/scan/{scanId}` based on the current route context.
 *
 * @example
 * // Instead of:
 * const storeId = useStoreId()
 * const scanId = useScanId()
 * httpClient.get(`/api/store/${storeId}/scan/${scanId}/products`)
 *
 * // Simply use:
 * const { get } = useScanContextAwareHttpClient()
 * get('/products')
 */
function useScanContextAwareHttpClient(): HttpClient & StoreContext {
  const storeId = useCurrentStoreId()
  const scanId = useScanId()

  const baseUrl = `/api/store/${storeId}/scan/${scanId}`

  return {
    storeId,
    scanId,
    get: async (url: UrlParam) => httpClient.get(`${baseUrl}${url}`),
    delete: async (url: UrlParam) => httpClient.delete(`${baseUrl}${url}`),
    post: async (url: UrlParam, data: RequestData) => httpClient.post(`${baseUrl}${url}`, data),
    put: async (url: UrlParam, data: RequestData) => httpClient.put(`${baseUrl}${url}`, data),
  }
}

export default useScanContextAwareHttpClient
